import React, { useState, useEffect, useMemo, useRef } from "react";
import MonthlySalesFilter from "../../../../../pages/reports/monthly-sales-commision/MonthlySalesFilter";
import MonthlySalesInvoiceDetailsModal from "../../../../modals/MonthlySalesInvoiceDetailsModal";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { useTable, useExpanded } from "react-table";
import { monthlyCommison, noDataPresent } from "../../../../../locales/en";
import { useDispatch, useSelector } from "react-redux";
import { getMonthlyCommissionReport } from "../../../../../redux/userReportsSlice";
import "./../../reportable/reportable.scss";
import TableSkeleton from "../../../../skeletons/TableSkeleton";
import { useVisibleColumnCount } from "../../../../../hooks/useVisibleColumnCount";
import ErrorBoundary from "../../../../error-boundary";
import ListViewFallback from "../../../../error-boundary/fallback/ListViewFallback";
import { PERIOD_BY_VIEWTYPE } from "../../../../../utils/getDropdownOptions";
import "../../../../../assets/scss/common.scss";
import {
  MONTH_12,
  MONTH_LABEL
} from "../../../../../utils/constants/appConstants";

const MonthlySalesCommissionTable = () => {
  const dispatch = useDispatch();
  const invoiceIdsListRef = useRef(null);
  const { headerRef } = useVisibleColumnCount();
  const { monthlyCommissionLoader } = useSelector((state) => state.userReport);
  const [columns, setColumns] = useState([]);
  const memoizedColumns = useMemo(() => columns, [columns]);
  const [data, setData] = useState({});
  const [title, setTitle] = useState({});
  const initialCommissionReport = {
    viewType: MONTH_LABEL,
    salesLeadName: "",
    period: MONTH_12,
    country: ""
  };
  const [commissionReport, setCommissionReport] = useState(
    initialCommissionReport
  );
  const [apiResponseData, setApiResponseData] = useState(null);

  const [addInvDtlsModal, setAddInvDtlsModal] = useState(false);
  const toggleAddInvDtlsModal = () => {
    setAddInvDtlsModal(!addInvDtlsModal);
  };

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const payload = {
          viewType: commissionReport.viewType,
          salesLead: commissionReport.salesLeadName,
          periods: commissionReport.period,
          country: commissionReport.country
        };
        const response = await dispatch(
          getMonthlyCommissionReport(payload)
        ).unwrap();
        const reportData = response.data.data;
        setApiResponseData(reportData);

        if (reportData && reportData.rows && reportData.columns) {
          const headerRow = {
            label: reportData.reportTitle || ""
          };
          reportData.columns.forEach((col) => {
            headerRow[col] = "";
          });

          const transformedData = reportData.rows.map((row) => {
            const obj = { label: row.label };
            reportData.columns.forEach((col, index) => {
              obj[col] = row.values[index];
            });
            return obj;
          });

          const finalData = [...transformedData];

          const dynamicColumns = [
            { Header: "", accessor: "label" },
            ...reportData.columns.map((col) => ({
              Header: col,
              accessor: col
            }))
          ];

          setColumns(dynamicColumns);
          setData(finalData);
          setTitle(headerRow);
        }
      } catch {
        // handle catch here;
      }
    };
    loadUsers();
  }, [
    commissionReport.viewType,
    commissionReport.period,
    commissionReport.country,
    commissionReport.salesLeadName,
    dispatch
  ]);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    useTable(
      {
        columns: memoizedColumns,
        data
      },
      useExpanded,
      (hooks) => {
        hooks.visibleColumns.push((columns) => [...columns]);
      }
    );

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setCommissionReport((prev) => {
      if (name === "viewType") {
        return {
          ...prev,
          viewType: value,
          period: PERIOD_BY_VIEWTYPE[value] ?? ""
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const expectedColumnCount = useMemo(() => {
    return columns.length || 13;
  }, [columns]);

  const handleCellClick = (rowIndex, colAccessor) => {
    if (!apiResponseData) return;
    const clickedRow = apiResponseData.rows[rowIndex];
    const invoiceId =
      clickedRow.invoiceIds[
        columns.findIndex((col) => col.accessor === colAccessor) - 1
      ];
    invoiceIdsListRef.current = invoiceId;
    setAddInvDtlsModal(true);
  };

  // Zero-value cells have no invoices to drill into, so they aren't links
  const renderValueCell = (cell, row, isZero) => {
    if (isZero) {
      return cell.render("Cell");
    }

    return (
      <Link onClick={() => handleCellClick(row.index, cell.column.id)}>
        {cell.render("Cell")}
      </Link>
    );
  };

  return (
    <>
      <div className="mt-3 mb-3">
        <MonthlySalesFilter
          commissionReport={commissionReport}
          onInputChange={handleInputChange}
          loading={monthlyCommissionLoader}
        />
      </div>
      <div className="col-md-12">
        <ErrorBoundary fallback={<ListViewFallback />}>
          <div className="table-responsive">
            <table
              className={`mt-2 mb-3 monthly-report-table bgless_table`}
              {...getTableProps()}
            >
              <thead ref={headerRef}>
                {headerGroups.map((headerGroup) => {
                  const { key, ...rest } = headerGroup.getHeaderGroupProps();
                  return (
                    <tr className="parentrow" {...rest} key={key}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps()} key={column.id}>
                          <span>{column.render("Header")}</span>
                        </th>
                      ))}
                    </tr>
                  );
                })}
                {monthlyCommissionLoader ? null : (
                  <tr className="parentrow">
                    <th>{title.label}</th>
                    {columns.slice(1).map((col) => (
                      <th key={col.accessor} />
                    ))}
                  </tr>
                )}
              </thead>
              {monthlyCommissionLoader ? (
                <tbody>
                  <TableSkeleton rows={8} columns={expectedColumnCount} />
                </tbody>
              ) : (
                <tbody {...getTableBodyProps()}>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length + 2} className="text-center">
                        {noDataPresent}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      prepareRow(row);
                      return (
                        <React.Fragment key={row.id}>
                          <tr className="parentrow" {...row.getRowProps()}>
                            {row.cells.map((cell, index) => {
                              const tooltipContents =
                                monthlyCommison?.tooltipList;
                              const tooltipContent =
                                index === 0 ? tooltipContents[row.index] : "";

                              const isZero =
                                cell.value === 0 ||
                                cell.value === "$0" ||
                                cell.value === "0";

                              return (
                                <td
                                  key={cell.getCellProps().key}
                                  {...cell.getCellProps()}
                                  className={`${index === 0 ? "fontWeightBold" : "fontWeightNormal"}`}
                                  {...(index === 0
                                    ? {
                                        "data-tooltip-id": `tooltip-${row.index}`,
                                        "data-tooltip-content": tooltipContent
                                      }
                                    : {})}
                                >
                                  {index === 0 ? (
                                    <>
                                      {cell.render("Cell")}
                                      {tooltipContent && (
                                        <Tooltip
                                          id={`tooltip-${row.index}`}
                                          className="report_tooltip"
                                        />
                                      )}
                                    </>
                                  ) : (
                                    renderValueCell(cell, row, isZero)
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              )}
            </table>
          </div>
        </ErrorBoundary>
      </div>

      <MonthlySalesInvoiceDetailsModal
        isOpen={addInvDtlsModal}
        toggle={toggleAddInvDtlsModal}
        invoiceIdsListRef={invoiceIdsListRef}
      />
    </>
  );
};

export default MonthlySalesCommissionTable;
